// The page's side of the PWA: registers our service worker on the app host
// only (the admin host never gets one, so Cloudflare Access sees every admin
// navigation, DECISIONS §10), and keeps hold of the browser's install prompt
// for the Home Screen hint. Updates wait for the next launch rather than
// reloading under someone's hands; an installed app is relaunched often
// enough.
export const isAdminHost = (hostname = location.hostname) => hostname.startsWith('admin');

/** Chrome's (and Edge's) install prompt; lib.dom has no type for it yet. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => { for (const listener of listeners) listener(); };

/** The install prompt the browser offered and we held back, until it is spent or the app is installed. For useSyncExternalStore. */
export const installPrompt = {
  get: (): BeforeInstallPromptEvent | null => deferred,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  /** Shows the browser's own install dialog; the prompt is spent either way. */
  async show(): Promise<'accepted' | 'dismissed'> {
    const event = deferred;
    if (!event) return 'dismissed';
    deferred = null;
    notify();
    await event.prompt();
    return (await event.userChoice).outcome;
  },
};

export function registerServiceWorker(): void {
  // Chrome fires beforeinstallprompt once, early, and only on a page that is
  // not yet installed: catch it here, before the lazy home chunk could.
  addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  addEventListener('appinstalled', () => { deferred = null; notify(); });

  if (!('serviceWorker' in navigator) || isAdminHost() || import.meta.env.DEV) return;
  addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // Offline on first visit, or a private window that refuses: the app still runs.
    });
  });
}
